<script lang="ts">import { blurOnClick } from '../../../../js-v2/blurOnClick';
import { getDarkMode } from '../../../../js-v2/stores/dark-mode-stores';
import { getTracking } from '../../../../js-v2/tracking';
import { trackLinkClick } from '../../../../js-v2/trackLinkClick';
const { id = '', title = '', navItems = [], alignment = 'left', containerSize = 'small', slideInFrom = 'top', showTitle = false, } = $props();
// Constants
const IMAGE_FORMATS = ['avif', 'webp'];
const trackFn = getTracking();
const darkModeStore = getDarkMode();
function getImage(imageListLink) {
    return $darkModeStore && imageListLink.props?.darkModeImage
        ? imageListLink.props.darkModeImage
        : imageListLink.props?.image;
}
</script>

<div class="ImageListCategory" data-entry-id={id}>
  {#if showTitle}
    <span class="title slide-in-from-{slideInFrom}" style:--image-list-item-number={1}>
      {title}
    </span>
  {/if}
  <ul class="list-container alignment-{alignment} {containerSize}-list-container">
    <ul class="image-tile-list slide-in-from-{slideInFrom}">
      {#each navItems as navItem, index}
        <li style:--image-list-item-number={index + (showTitle ? 2 : 1)}>
          <a
            class="image-tile"
            data-entry-id={navItem.contentfulEntryId}
            href={navItem.props?.url}
            use:blurOnClick
            use:trackLinkClick={{
              id: navItem.contentfulEntryId,
              title: `${navItem.props?.title} ${navItem.props?.subtitle || ''}`.trim(),
              url: navItem.props?.url,
              trackFn,
            }}
          >
            {#if getImage(navItem)}
              <picture>
                {#each IMAGE_FORMATS as format}
                  <source srcset={`${getImage(navItem).props?.file?.url}?fm=${format}`} type="image/{format}" />
                {/each}
                <img
                  data-entry-id={getImage(navItem).contentfulEntryId}
                  class="image"
                  fetchpriority="high"
                  loading="lazy"
                  alt={getImage(navItem).props?.description}
                  src={getImage(navItem).props?.file?.url}
                />
              </picture>
            {/if}
            <div class="image-tile-text-container">
              {#if navItem.props?.title}<span class="image-title">{navItem.props?.title}</span>{/if}
              {#if navItem.props?.subtitle}<span class="image-subtitle">{navItem.props?.subtitle}</span>{/if}
            </div>
          </a>
        </li>
      {/each}
    </ul>
  </ul>
</div>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.ImageListCategory .title {
  --letter-spacing: var(--text-variant-50-letter-spacing);
  --line-height: var(--text-variant-50-line-height);
  --font-size: var(--text-variant-50-font-size);
  --font-weight: var(--text-variant-50-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-50-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  display: block;
  width: max-content;
  color: var(--color-neutral-400);
  margin-bottom: 20px;
}
.ImageListCategory .title.slide-in-from-top {
  opacity: 0;
  transform: translateX(0) translateY(-4px);
  animation: fade-in-slide-from-top 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--image-list-item-number) * 20ms);
}
.ImageListCategory .title.slide-in-from-left {
  opacity: 0;
  transform: translateX(-4px) translateY(0);
  animation: fade-in-slide-from-left 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--image-list-item-number) * 20ms);
}
.ImageListCategory .list-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.ImageListCategory .list-container.small-list-container {
  max-width: 780px;
}
.ImageListCategory .list-container.large-list-container {
  max-width: 950px;
}
.ImageListCategory .list-container.alignment-left {
  align-items: flex-start;
}
.ImageListCategory .list-container.alignment-left .image-tile-list {
  justify-content: flex-start;
}
.ImageListCategory .list-container.alignment-center {
  align-items: center;
}
.ImageListCategory .list-container.alignment-center .image-tile-list {
  justify-content: center;
}
.ImageListCategory ul {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
}
.ImageListCategory ul > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.ImageListCategory ul > li::before {
  display: none;
}
.ImageListCategory ul > li :global(:last-child) {
  margin: 0;
}
.ImageListCategory .image-tile-list {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(124px, 1fr));
  flex: 1;
  width: 100%;
}
.ImageListCategory .image-tile-list.slide-in-from-top li {
  opacity: 0;
  transform: translateX(0) translateY(-4px);
  animation: fade-in-slide-from-top 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--image-list-item-number) * 20ms);
}
.ImageListCategory .image-tile-list.slide-in-from-left li {
  opacity: 0;
  transform: translateX(-4px) translateY(0);
  animation: fade-in-slide-from-left 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--image-list-item-number) * 20ms);
}
.ImageListCategory .image-tile-list .image-tile {
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  min-width: 124px;
  height: 120px;
  text-decoration: none;
  padding: 10px;
}
.ImageListCategory .image-tile-list .image-tile::-moz-focus-inner {
  border: 0;
}
.ImageListCategory .image-tile-list .image-tile:focus {
  outline: none;
}
.ImageListCategory .image-tile-list .image-tile::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight);
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.ImageListCategory .image-tile-list .image-tile:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
@media (hover: hover) {
  .ImageListCategory .image-tile-list .image-tile:hover::before {
    opacity: 1;
    transform: scale(1);
  }
}
.ImageListCategory .image-tile-list .image-tile > * {
  position: relative;
}
@media (min-width: 64rem) {
  .ImageListCategory .image-tile-list .image-tile {
    min-width: 124px;
  }
}
.ImageListCategory .image-tile-list .image-tile picture {
  height: 64px;
}
.ImageListCategory .image-tile-list .image-tile .image {
  width: 73px;
  height: 64px;
  object-fit: cover;
}
.ImageListCategory .image-tile-list .image-tile .image-tile-text-container {
  padding-top: 10px;
}
.ImageListCategory .image-tile-list .image-tile .image-tile-text-container .image-title,
.ImageListCategory .image-tile-list .image-tile .image-tile-text-container .image-subtitle {
  display: block;
  text-align: center;
}
.ImageListCategory .image-tile-list .image-tile .image-tile-text-container .image-title {
  white-space: nowrap;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: normal;
  font-weight: var(--text-font-weight-medium);
  font-family: var(--text-font-family);
  color: var(--color-text);
}
.ImageListCategory .image-tile-list .image-tile .image-tile-text-container .image-subtitle {
  white-space: nowrap;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: normal;
  font-weight: var(--text-font-weight-medium);
  font-family: var(--text-font-family);
  color: var(--color-text);
  margin-top: 4px;
  letter-spacing: normal;
  font-weight: var(--text-font-weight-regular);
}</style>
